import React, { useCallback, memo } from 'react';
import isEqual from 'lodash/isEqual';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { MutationUpdaterFn } from 'apollo-client';

import { AvatarItem } from '@atlaskit/avatar';
import Lozenge from '@atlaskit/lozenge';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';

import { EDITOR_ONBOARDING_BLUEPRINT_KEY } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { LiveEditLozenge } from '@confluence/live-pages-features/entry-points/LiveEditLozenge';

import { TemplateIcon } from './TemplateIcon';
import { Star } from './Star';
import {
	Title,
	Description,
	TemplateCardWrapper,
	ItemContainer,
	AvatarContainer,
	TitleContainer,
	DescriptionContainer,
} from './TemplateCardStyles';

export type TemplateCardTemplate = {
	name: string | null;
	description: string | null;
	iconURL: string | null;
	darkModeIconURL?: string | null;
	styleClass: string | null;
	itemModuleCompleteKey: string | null;
	blueprintModuleCompleteKey: string | null;
	contentBlueprintId: string | null;
	templateId: string | null;
	isFavourite?: boolean | null;
	recommendationRank?: number | null;
};

export type TemplateCardProps<T extends TemplateCardTemplate> = {
	template: T;
	onItemClick: (template: T, additionalStatAttributes: object) => void;
	isActive?: boolean;
	isDisabled?: boolean;
	onStarClick?: MutationUpdaterFn | undefined;
};

// React.memo loses generic typings: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-542793243
const typedMemo: <T>(c: T, propsAreEqual?: (prevProps: T, nextProps: T) => boolean) => T = memo;

const i18n = defineMessages({
	tutorial: {
		id: 'template-card.onboarding.tutorial',
		defaultMessage: 'Tutorial',
		description: 'Indicating the current template card to be a tutorial',
	},
});

export const TemplateCard = typedMemo(
	<T extends TemplateCardTemplate>({
		template,
		onItemClick,
		onStarClick,
		isDisabled,
		isActive,
	}: TemplateCardProps<T>) => {
		const title = template.name || '';
		const description = template.description || '';
		const recommendationRank = template.recommendationRank || null;
		const moduleKey = template.blueprintModuleCompleteKey || '';
		let titleComponent = (
			<TitleContainer>
				<Title>{title}</Title>
			</TitleContainer>
		);
		let descriptionComponent = <Description>{description}</Description>;

		const isOnboardingTemplate = moduleKey === EDITOR_ONBOARDING_BLUEPRINT_KEY;
		const isLivePageTemplate =
			template.blueprintModuleCompleteKey ===
			'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-live-page-blueprint';

		if (isOnboardingTemplate) {
			titleComponent = (
				<TitleContainer>
					<Title>{title}</Title>
					<Lozenge testId="tutorial-lozenge" appearance="new">
						<FormattedMessage {...i18n.tutorial} />
					</Lozenge>
				</TitleContainer>
			);
			descriptionComponent = (
				<DescriptionContainer>
					<Description>{description}</Description>
					<ShortcutIcon label="" size="small" testId="template-card-shortcut-icon" />
				</DescriptionContainer>
			);
		}

		if (isLivePageTemplate) {
			titleComponent = (
				<TitleContainer>
					<Title>{title}</Title>
					<LiveEditLozenge />
				</TitleContainer>
			);
		}

		const onClick = useCallback(() => {
			onItemClick && onItemClick(template, { recommendationRank });
		}, [onItemClick, template, recommendationRank]);

		// @todo CTE-337 remove isDisabled check for active highlight
		// when callback for close and create for dialog and drawer are in place
		const active = isActive && isDisabled;

		return (
			<AvatarContainer>
				<AvatarItem
					avatar={<TemplateIcon isDisabled={isDisabled} template={template} />}
					onClick={onClick}
					isDisabled={isDisabled}
					isTruncationDisabled
					primaryText={titleComponent}
					secondaryText={descriptionComponent}
				>
					{({ ref: _, ...props }) => <TemplateCardWrapper {...props} isActive={active} />}
				</AvatarItem>
				{isOnboardingTemplate ? null : (
					<ItemContainer>
						<Star isDisabled={isDisabled} template={template} onStarClick={onStarClick} />
					</ItemContainer>
				)}
			</AvatarContainer>
		);
	},
	isEqual,
);
